import { initializeApp, getApps, type FirebaseApp } from "firebase/app"
import { getAuth, onAuthStateChanged, type Auth, signOut } from "firebase/auth"
import AppConsts from "./app-consts"
import { TOKEN_NAME } from "@/api/client.ts"

let firebaseApp: FirebaseApp | null = null
let firebaseAuth: Auth | null = null

function getFirebaseConfig() {
  const env = (import.meta as { env?: Record<string, unknown> }).env || {}
  return {
    apiKey: env.VITE_FIREBASE_API_KEY as string | undefined,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
    projectId: env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  }
}

export function isFirebaseEnabled(): boolean {
  const config = getFirebaseConfig()
  return !!(config.apiKey && config.projectId)
}

export function getFirebaseAuth(): Auth | null {
  if (!isFirebaseEnabled()) return null

  if (!firebaseAuth) {
    const config = getFirebaseConfig()
    if (getApps().length === 0) {
      firebaseApp = initializeApp({
        apiKey: config.apiKey!,
        authDomain: config.authDomain ?? `${config.projectId}.firebaseapp.com`,
        projectId: config.projectId!,
      })
    } else {
      firebaseApp = getApps()[0]
    }
    firebaseAuth = getAuth(firebaseApp)
  }

  return firebaseAuth
}

export async function getFirebaseIdToken(): Promise<string | null> {
  const auth = getFirebaseAuth()
  if (!auth) return null

  const user = auth.currentUser
  if (!user) return null

  try {
    return await user.getIdToken()
  } catch {
    return null
  }
}

async function exchangeFirebaseTokenForAbpToken(
  idToken: string
): Promise<{ accessToken: string; encryptedAccessToken: string } | null> {
  try {
    const url = `${AppConsts.remoteServiceBaseUrl}/api/TokenAuth/FirebaseAuthenticate`
    const tenantId = abp?.multiTenancy?.getTenantIdCookie?.()
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }
    if (tenantId) {
      headers[abp.multiTenancy.tenantIdCookieName] = String(tenantId)
    }
    const cookieLang = abp?.utils?.getCookieValue?.(
      "Abp.Localization.CultureName"
    )
    if (cookieLang) {
      headers[".AspNetCore.Culture"] = `c=${cookieLang}|uic=${cookieLang}`
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ firebaseIdToken: idToken }),
    })

    if (!response.ok) return null

    const json = (await response.json()) as {
      result?: { accessToken: string; encryptedAccessToken: string }
    }
    return json.result ?? null
  } catch {
    return null
  }
}

export async function tryFirebaseAutoLogin(): Promise<boolean> {
  if (!isFirebaseEnabled()) return false

  const idToken = await getFirebaseIdToken()
  if (!idToken) return false

  const result = await exchangeFirebaseTokenForAbpToken(idToken)
  if (!result) return false

  try {
    abp.auth.setToken(result.accessToken)
    localStorage.setItem(
      AppConsts.authorization.encrptedAuthTokenName,
      JSON.stringify({ token: result.encryptedAccessToken })
    )
    return true
  } catch {
    return false
  }
}

export async function loginWithFirebasePopup(): Promise<boolean> {
  const auth = getFirebaseAuth()
  if (!auth) return false

  try {
    const { signInWithPopup, GoogleAuthProvider } =
      await import("firebase/auth")
    await signInWithPopup(auth, new GoogleAuthProvider())
    const idToken = await getFirebaseIdToken()
    if (!idToken) return false
    return idToken
    // console.log("dd", idToken)

    //
    // const result = await exchangeFirebaseTokenForAbpToken(idToken)
    // if (!result) return false
    //
    // abp.auth.setToken(result.accessToken)
    // localStorage.setItem(TOKEN_NAME, result.accessToken)
    // localStorage.setItem(
    //   AppConsts.authorization.encrptedAuthTokenName,
    //   JSON.stringify({ token: result.encryptedAccessToken })
    // )
    // return true
  } catch {
    return false
  }
}

export function waitForFirebaseAuthReady(): Promise<void> {
  const auth = getFirebaseAuth()
  if (!auth) return Promise.resolve()

  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe()
      resolve()
    })
  })
}

export async function logOut(): Promise<void> {
  const auth = getFirebaseAuth()
  if (auth) {
    await signOut(auth)
  }
}
